import React, { useState } from 'react';


import Icon from '../../components/AppIcon';
import NavigationHeader from '../homepage/components/NavigationHeader';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import DatasetCard from './components/DatasetCard';
import FilterPanel from './components/FilterPanel';
import DataPreviewModal from './components/DataPreviewModal';
import StatsOverview from './components/StatsOverview';
import RecentActivity from './components/RecentActivity';
import QuickActions from './components/QuickActions';

const ResearchDataPortal = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    accessLevel: 'all',
    format: 'all',
    dateRange: 'all',
    startDate: '',
    endDate: '',
    peerReviewed: false,
    validated: false,
    published: false,
    minSize: '',
    maxSize: ''
  });

  const stats = {
    totalDatasets: 1247,
    totalDownloads: 45823,
    activeResearchers: 892,
    publications: 156
  };

  const datasets = [
  {
    id: 1,
    title: "Biodiversity Assessment Survey 2023-2025",
    description: "Comprehensive species inventory and population monitoring data collected across all ecological zones of PPSRNP. Includes camera trap images, acoustic recordings, and field observation logs with GPS coordinates and temporal data.",
    thumbnail: "https://images.unsplash.com/photo-1561389745-97596a161957",
    thumbnailAlt: "Dense tropical rainforest canopy with morning mist and diverse vegetation layers showing rich biodiversity",
    category: "Biodiversity Data",
    accessLevel: "Public",
    format: "CSV, JSON",
    size: 524288000,
    lastUpdated: "2025-12-05",
    downloads: 1247,
    tags: ["Species Monitoring", "Camera Traps", "Field Surveys", "GPS Data"]
  },
  {
    id: 2,
    title: "Climate & Weather Monitoring Dataset",
    description: "Hourly meteorological data including temperature, humidity, rainfall, wind patterns, and atmospheric pressure collected from 12 weather stations distributed throughout the park. Three-year continuous monitoring period.",
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1a26264f0-1765106510364.png",
    thumbnailAlt: "Weather monitoring station equipment with sensors and data collection instruments in tropical forest setting",
    category: "Climate & Weather",
    accessLevel: "Public",
    format: "XLSX, CSV",
    size: 157286400,
    lastUpdated: "2025-12-06",
    downloads: 892,
    tags: ["Weather Data", "Climate Monitoring", "Meteorology", "Time Series"]
  },
  {
    id: 3,
    title: "Philippine Eagle Population Study",
    description: "Detailed tracking data of critically endangered Philippine Eagles including nesting sites, hunting territories, breeding success rates, and genetic samples. Restricted access requires research credentials and ethical approval.",
    thumbnail: "https://images.unsplash.com/photo-1613482827805-e5c62f54bb0e",
    thumbnailAlt: "Majestic Philippine Eagle perched on tree branch with distinctive crest feathers and powerful talons visible",
    category: "Species Monitoring",
    accessLevel: "Restricted",
    format: "GeoJSON, CSV",
    size: 209715200,
    lastUpdated: "2025-11-28",
    downloads: 234,
    tags: ["Endangered Species", "Eagles", "Tracking Data", "Conservation"]
  },
  {
    id: 4,
    title: "Habitat Quality Assessment Maps",
    description: "High-resolution spatial analysis of habitat conditions across PPSRNP including vegetation density, canopy cover, water sources, and human disturbance indicators. Generated using satellite imagery and ground-truthing data.",
    thumbnail: "https://images.unsplash.com/photo-1596664096995-3428b97b107f",
    thumbnailAlt: "Aerial view of pristine tropical forest showing varied canopy heights and natural river systems",
    category: "Habitat Assessment",
    accessLevel: "Public",
    format: "GeoJSON, SHP",
    size: 419430400,
    lastUpdated: "2025-12-01",
    downloads: 567,
    tags: ["GIS", "Habitat Mapping", "Remote Sensing", "Spatial Analysis"]
  },
  {
    id: 5,
    title: "Threat Monitoring & Incident Reports",
    description: "Documented threats to park integrity including illegal logging attempts, poaching incidents, fire events, and invasive species observations. Includes ranger patrol logs, incident photographs, and response actions taken.",
    thumbnail: "https://images.unsplash.com/photo-1610849450468-97b1a828464f",
    thumbnailAlt: "Park ranger in uniform conducting forest patrol with GPS device and documentation equipment",
    category: "Threat Analysis",
    accessLevel: "Confidential",
    format: "PDF, CSV",
    size: 104857600,
    lastUpdated: "2025-12-07",
    downloads: 89,
    tags: ["Security", "Threats", "Patrol Data", "Incidents"]
  },
  {
    id: 6,
    title: "Water Quality Monitoring Dataset",
    description: "Chemical and biological water quality parameters from the underground river system and surface water bodies. Includes pH, dissolved oxygen, nutrient levels, bacterial counts, and heavy metal analysis conducted monthly.",
    thumbnail: "https://images.unsplash.com/photo-1704489867616-9fcac1dea981",
    thumbnailAlt: "Crystal clear underground river water flowing through limestone cave formations with natural light filtering through",
    category: "Conservation Impact",
    accessLevel: "Public",
    format: "XLSX, CSV",
    size: 52428800,
    lastUpdated: "2025-12-03",
    downloads: 445,
    tags: ["Water Quality", "Chemistry", "River Monitoring", "Environmental Health"]
  },
  {
    id: 7,
    title: "Visitor Impact Assessment Study",
    description: "Analysis of tourism effects on wildlife behavior, vegetation trampling, noise pollution levels, and waste generation patterns. Includes before-after comparisons and carrying capacity recommendations for sustainable tourism management.",
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1115dcc87-1764847817033.png",
    thumbnailAlt: "Eco-tourists walking on designated trail through forest with guide maintaining safe distance from wildlife",
    category: "Conservation Impact",
    accessLevel: "Public",
    format: "PDF, XLSX",
    size: 78643200,
    lastUpdated: "2025-11-25",
    downloads: 312,
    tags: ["Tourism Impact", "Sustainability", "Carrying Capacity", "Visitor Management"]
  },
  {
    id: 8,
    title: "Endemic Species Distribution Maps",
    description: "Spatial distribution data for 47 endemic species found exclusively in PPSRNP including plants, mammals, birds, reptiles, and amphibians. Includes habitat preference analysis and population density estimates with seasonal variations.",
    thumbnail: "https://images.unsplash.com/photo-1575550182013-e8db3541848c",
    thumbnailAlt: "Rare endemic plant species with distinctive purple flowers growing on limestone cliff face in natural habitat",
    category: "Biodiversity Data",
    accessLevel: "Restricted",
    format: "GeoJSON, CSV",
    size: 262144000,
    lastUpdated: "2025-11-30",
    downloads: 178,
    tags: ["Endemic Species", "Distribution", "Biogeography", "Conservation Priority"]
  }];


  const recentActivities = [
  {
    id: 1,
    userName: "Dr. Maria Santos",
    userAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d467c84a-1763301569143.png",
    userAvatarAlt: "Professional headshot of Filipino female researcher with glasses and white lab coat smiling warmly",
    action: "downloaded",
    dataset: "Biodiversity Assessment Survey",
    type: "download",
    timestamp: "2 hours ago"
  },
  {
    id: 2,
    userName: "Prof. Juan Cruz",
    userAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_124779a74-1763292928835.png",
    userAvatarAlt: "Professional portrait of male professor with gray beard wearing formal blue shirt in academic setting",
    action: "uploaded",
    dataset: "Soil Composition Analysis",
    type: "upload",
    timestamp: "5 hours ago"
  },
  {
    id: 3,
    userName: "Dr. Lisa Chen",
    userAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1e6b5beba-1763295037633.png",
    userAvatarAlt: "Asian female scientist with short black hair in field research attire with binoculars around neck",
    action: "cited",
    dataset: "Philippine Eagle Population Study",
    type: "citation",
    timestamp: "1 day ago"
  },
  {
    id: 4,
    userName: "Research Team UPLB",
    userAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1374be66c-1764851173928.png",
    userAvatarAlt: "Young male researcher in khaki field vest holding tablet device in tropical forest environment",
    action: "updated",
    dataset: "Climate Monitoring Dataset",
    type: "update",
    timestamp: "2 days ago"
  }];


  const sortOptions = [
  { value: 'recent', label: 'Most Recent' },
  { value: 'popular', label: 'Most Downloaded' },
  { value: 'title', label: 'Title (A-Z)' },
  { value: 'size', label: 'File Size' }];


  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      category: 'all',
      accessLevel: 'all',
      format: 'all',
      dateRange: 'all',
      startDate: '',
      endDate: '',
      peerReviewed: false,
      validated: false,
      published: false,
      minSize: '',
      maxSize: ''
    });
  };

  const handleApplyFilters = () => {
    console.log('Applying filters:', filters);
  };

  const handleDownload = (dataset) => {
    console.log('Downloading dataset:', dataset?.title);
  };

  const handlePreview = (dataset) => {
    setSelectedDataset(dataset);
  };

  const handleQuickAction = (actionId) => {
    console.log('Quick action:', actionId);
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      {/* Navigation Header */}
      <NavigationHeader />
      
      {/* Add padding to account for fixed header */}
      <div className="pt-20">
        <div className="bg-gradient-conservation py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-lg bg-[var(--color-primary)] flex items-center justify-center">
                <Icon name="Database" size={24} className="text-[var(--color-primary-foreground)]" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-white mb-2">Research Data Portal</h1>
                <p className="text-white/80 text-lg">Scientific Data Access & Conservation Research Hub</p>
              </div>
            </div>
            <p className="text-white/90 max-w-3xl">
              Access comprehensive biodiversity datasets, monitoring reports, and collaborative research opportunities. Advanced filtering, bulk downloads, and API integration for conservation researchers worldwide.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8">
          <StatsOverview stats={stats} />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
            <div className="lg:col-span-2 space-y-8">
              <div className="bg-[var(--color-card)] rounded-lg p-6 border border-[var(--color-border)]">
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <Input
                      type="search"
                      placeholder="Search datasets by title, keywords, or tags..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e?.target?.value)} />

                  </div>
                  <div className="flex gap-3">
                    <Select
                      options={sortOptions}
                      value={sortBy}
                      onChange={setSortBy}
                      className="w-48" />

                    <Button
                      variant={showFilters ? 'default' : 'outline'}
                      iconName="Filter"
                      iconPosition="left"
                      onClick={() => setShowFilters(!showFilters)}>

                      Filters
                    </Button>
                  </div>
                </div>

                {showFilters &&
                <div className="mb-6">
                    <FilterPanel
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onReset={handleResetFilters}
                    onApply={handleApplyFilters} />

                  </div>
                }

                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-[var(--color-muted-foreground)]">
                    Showing {datasets?.length} of {stats?.totalDatasets} datasets
                  </p>
                  <Button variant="ghost" size="sm" iconName="RefreshCw">
                    Refresh
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6">
                {datasets?.map((dataset) =>
                <DatasetCard
                  key={dataset?.id}
                  dataset={dataset}
                  onDownload={handleDownload}
                  onPreview={handlePreview} />

                )}
              </div>

              <div className="flex justify-center">
                <Button variant="outline" iconName="ChevronDown" iconPosition="right">
                  Load More Datasets
                </Button>
              </div>
            </div>

            <div className="space-y-6">
              <QuickActions onAction={handleQuickAction} />
              <RecentActivity activities={recentActivities} />
            </div>
          </div>
        </div>
      </div>
      {selectedDataset &&
      <DataPreviewModal
        dataset={selectedDataset}
        onClose={() => setSelectedDataset(null)} />

      }
    </div>
  );
};

export default ResearchDataPortal;